<template>
  <f7-page class="home_page" name="home_page">
    <f7-navbar v-if="isSmallScreen" :outline="false">
      <f7-nav-title v-if="theme.md" class="padding-horizontal-half" href="/">
        <img src="/512x512.svg" :alt="$t('common.a11y.appLogo')" class="avocash_logo" width="32" height="32" style="vertical-align: middle; margin-right: 8px;" />
        <avo_name />
      </f7-nav-title>
      <f7-nav-left v-if="theme.ios" class="padding-horizontal-half" href="/">
        <img src="/512x512.svg" :alt="$t('common.a11y.appLogo')" class="avocash_logo" width="32" height="32" style="vertical-align: middle; margin-right: 8px;" />
        <avo_name />
      </f7-nav-left>
      <f7-nav-right>
        <WalletChip v-if="keycloakStore.authenticated && walletBalance" :amount="walletBalance.amount" :currency="walletBalance.currency" />
        <f7-link
          v-if="isSmallScreen"
          class="searchbar-enable"
          data-searchbar=".searchbar-feed"
          icon-ios="f7:search"
          icon-md="material:search"
        ></f7-link>
        <navbarright />
      </f7-nav-right>
      <f7-searchbar
        v-if="isSmallScreen"
        class="searchbar-feed"
        expandable
        custom-search
        :placeholder="$t('common.search')"
        :clear-button="true"
        :value="feedStore.searchQuery"
        @input="onSearchInput"
        @searchbar:clear="onSearchClear"
        @searchbar:disable="onSearchDisable"
      ></f7-searchbar>
    </f7-navbar>

    <AppLayout>
      <template #main>
        <!-- ═══ LOGGED-IN STATE ═══ -->
        <template v-if="keycloakStore.authenticated">
          <avocashOrganicWealthBanner :wallet-balance="walletBalance" :active-groups-count="4" />
          <QuickActions :actions="quickActions" @select="onQuickAction" />
          <HomeFeed :cards="feedCards" :filters="feedFilters" />

          <div class="margin-horizontal-half">
            <myGroups />
          </div>
        </template>

        <!-- ═══ LOGGED-OUT STATE ═══ -->
        <template v-else>
          <CtaMessageCard
            variant="hero"
            icon="person_3_fill"
            :title="$t('cta.home.title')"
            :description="$t('cta.home.desc')"
            :button-label="$t('home.signup.createFree')"
            :link-label="heroLoginLabel"
            gradient
            @action="registerPrompt"
            @link="loginPrompt"
          />
          <DiscoveryGrid />
          <HomeFeed :cards="publicFeedCards" :filters="publicFilters" />
        </template>

      </template>
    </AppLayout>

    <!-- <AdaptiveSheet
      v-model:opened="createSheetOpened"
      class="create-sheet"
      :close-on-escape="true"
      swipe-to-close
      close-by-outside-click
      :sheet-style="sheetStyle"
    >
      <f7-block-title class="create-sheet-title">{{ $t('homepage.createSheet.title') }}</f7-block-title>
      <f7-block class="create-sheet-options">
        <f7-link
          v-for="option in createSheetOptions"
          :key="option.id"
          class="create-sheet-option"
          @click="onCreateOption(option)"
        >
          <span class="create-sheet-option-icon" :class="'create-sheet-option-icon--' + option.color">
            <f7-icon :f7="option.icon" size="16" />
          </span>
          <span class="create-sheet-option-label">{{ option.label }}</span>
          <f7-icon f7="chevron_right" class="create-sheet-option-chevron" />
        </f7-link>
      </f7-block>
    </AdaptiveSheet> -->
  </f7-page>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { f7, theme } from 'framework7-vue'
import i18n from '../js/i18n.js'
import avo_name from '../components/avocash_name.vue'
import navbarright from '../components/navbarright.vue'
import myGroups from '../components/myGroups.vue'
import WalletChip from '../components/home/WalletChip.vue'
import QuickActions from '../components/home/QuickActions.vue'
import avocashOrganicWealthBanner from '../components/home/avocashOrganicWealthBanner.vue'
import CtaMessageCard from '../components/home/CtaMessageCard.vue'
import DiscoveryGrid from '../components/home/DiscoveryGrid.vue'
import HomeFeed from '../components/home/HomeFeed.vue'
import AppLayout from '../components/common/AppLayout.vue'
import AdaptiveSheet from '../components/common/AdaptiveSheet.vue'
import { useKeycloakStore } from '../js/pinia-stores/keycloak.js'
import { useAccountStore } from '../js/pinia-stores/account.js'
import { useAuthStore } from '../js/pinia-stores/auth.js'
import { useSmallScreen } from '../js/composables/useMediaQuery.js'
import { mockPublicFeedCards } from '../data/mockHomeFeed.js'
import { useFeedStore } from "../js/pinia-stores/feed.js";
import { useHaptics } from "../js/composables/useHaptics.js";

const keycloakStore = useKeycloakStore()
const accountStore = useAccountStore()
const authStore = useAuthStore()
const feedStore = useFeedStore()
const haptic = useHaptics();
const { t } = i18n.global

const isSmallScreen = useSmallScreen().matches

const heroLoginLabel = computed(() => `${t('home.signup.haveAccount')} ${t('home.signup.login')}`)

const walletBalance = computed(() => {
  const ua = accountStore.userAccount
  if (!ua || !ua.userAccountWallets) return null
  const wallets = [
    ...(ua.userAccountWallets.STANDARD || []),
    ...(ua.userAccountWallets.BUSINESS || []),
  ]
  const main = wallets.find((w) => w.main) || wallets[0] || null
  if (!main || main.balance === undefined || main.balance === null) return null
  return { amount: Number(main.balance), currency: main.currency || 'CFA' }
})

const quickActions = computed(() => [
  { id: 'qa-send', icon: 'paperplane_fill', label: t('homepage.quickActions.contribute'), route: '/recipients', color: 'green' },
  { id: 'qa-groups', icon: 'person_3_fill', label: t('homepage.quickActions.groups'), route: '/groups/', color: 'blue' },
  { id: 'qa-market', icon: 'bag_fill', label: t('homepage.quickActions.market'), route: '/shops', color: 'purple' },
  { id: 'qa-create', icon: 'plus_circle_fill', label: t('homepage.quickActions.create'), color: 'gold' },
])

const createSheetOpened = ref(false)

const createSheetOptions = computed(() => [
  { id: 'group', icon: 'person_3_fill', label: t('homepage.createSheet.group'), route: '/group/new', color: 'blue' },
  { id: 'shop', icon: 'bag_fill', label: t('homepage.createSheet.shop'), route: '/shop/new', color: 'purple' },
  { id: 'campaign', icon: 'gift_fill', label: t('homepage.createSheet.campaign'), route: '/groups/', color: 'gold' },
])

const sheetStyle = "--f7-sheet-bg-color: #fff; height: 60vh;"

function onQuickAction(action) {
  if (action.id === 'qa-create') {
    openCreatOptionActionsheet();
    return
  }
  if (action.route) {
    f7.views.main.router.navigate(action.route)
  }
}

function onCreateOption(option) {
  createSheetOpened.value = false
  if (option.route) {
    f7.views.main.router.navigate(option.route)
  }
}
function openCreatOptionActionsheet() {
   haptic.light();
  const buttons = [
     [{ text: t('homepage.createSheet.title') ,label:true}],
    createSheetOptions.value.map((option) => ({
      text: option.label,
      icon:`<i class="f7-icons" style="color:${option.color};" >${option.icon}</i>`,
      color: option.color,
      onClick: () => {
        haptic.light();
        onCreateOption(option);
      },
    })),
  
    [{ text: t("common.cancel"), bold: true }],
  ];
  const actions = f7.actions.create({
    // openIn: "sheet",
    convertToPopover: true,
    closeOnEscape: true,
    closeByOutsideClick: true,
    buttons,
    arrow:true,
    targetEl: f7.$('.qa-itemqa-create'),
    on: {
      closed: () => actions.destroy(),
    },
  });
  actions.open();
}

const feedFilters = computed(() => [
  { id: 'all', label: t('feed.filters.all') },
  { id: 'posts', label: t('feed.filters.posts') },
  { id: 'campaigns', label: t('feed.filters.campaigns') },
  { id: 'polls', label: t('feed.filters.polls') },
  { id: 'sponsored', label: t('feed.filters.sponsored') },
])

const feedCards = []

const publicFeedCards = ref(mockPublicFeedCards)

const publicFilters = computed(() => [
  { id: 'all', label: t('feed.filters.all') },
  { id: 'posts', label: t('feed.filters.posts') },
  { id: 'sponsored', label: t('feed.filters.sponsored') },
])

onMounted(() => {
  if (keycloakStore.authenticated && accountStore.userAccount === null) {
    useAccountStore().loadAccount(authStore.profile?.sub)
  }
})
function onSearchInput(event) {
  feedStore.setSearchQuery(event.target.value);
}

function onSearchClear() {
  feedStore.clearSearch();
}

function onSearchDisable() {
  feedStore.clearSearch();
}

function registerPrompt() {
  keycloakStore.keycloak.register();
}

function loginPrompt() {
  keycloakStore.keycloak.login();
}
</script>

<style scoped>
/* Scoped layout handles are centralized in AppLayout. */
.create-sheet {
  padding: 16px 16px 24px;
}
.create-sheet-title {
  margin-bottom: 4px;
  font-size: 15px;
  font-weight: 600;
}
.create-sheet-options {
  margin: 0;
  padding: 0;
}
.create-sheet-option {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 4px;
  background: transparent;
  border: none;
  border-bottom: 0.5px solid var(--f7-list-item-border-color, rgba(0, 0, 0, 0.08));
  cursor: pointer;
  text-align: left;
}
.create-sheet-option:last-child {
  border-bottom: none;
}
.create-sheet-option-icon {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.create-sheet-option-icon--blue {
  background: var(--avocash-blue-light, #EBF0FC);
  color: var(--avocash-blue, #3B4FCA);
}
.create-sheet-option-icon--purple {
  background: var(--avocash-purple-light, #F5F0FF);
  color: var(--avocash-purple, #6B46C1);
}
.create-sheet-option-icon--gold {
  background: var(--avocash-gold-light, #FDF6E3);
  color: var(--avocash-gold, #C9A84C);
}
.create-sheet-option-label {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: var(--f7-text-color, #000);
}
.create-sheet-option-chevron {
  color: var(--f7-on-surface-variant, #8e8e93);
}
</style>